import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const menuVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const menus = [
  {
    path: "",
    name: "Home Page",
    description: "Return to the home page.",
  },
  {
    path: "decision",
    name: "Make a Decision",
    description:
      "Need help deciding something? This tool will help you reach a conclusion through guided questions.",
  },
  {
    path: "cookbook",
    name: "Cookbook",
    description: "Browse delicious recipes and adjust portions dynamically.",
  },
];

export const AppMenu: React.FC = () => {
  const whichMenu = () => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      // Get the first part of the path after the slash, or default to "" for the main page.
      const page = path.split("/")[1] || "";
      return page;
    }
    return "";
  };
  const [currentPage, setCurrentPage] = useState<string>(whichMenu());
  const [showTooltips, setShowTooltips] = useState<Record<string, boolean>>({});
  const hoverTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const handleMouseEnter = (path: string) => {
    hoverTimers.current[path] = setTimeout(() => {
      setShowTooltips((prev) => ({
        ...prev,
        [path]: true,
      }));
    }, 800); // 800ms delay before showing tooltip
  };

  const handleMouseLeave = (path: string) => {
    // Clear the timer if mouse leaves before tooltip is shown
    if (hoverTimers.current[path]) {
      clearTimeout(hoverTimers.current[path]);
      delete hoverTimers.current[path];
    }

    // Hide the tooltip
    setShowTooltips((prev) => ({
      ...prev,
      [path]: false,
    }));
  };

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      Object.values(hoverTimers.current).forEach((timer) =>
        clearTimeout(timer)
      );
    };
  }, []);

  return (
    <TooltipProvider>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="flex top-4 right-4 w-10 h-10 p-0 bg-background hover:bg-accent"
          >
            <Menu className="h-12 w-12" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-background">
          {menus.map((menu) => (
            <Tooltip
              key={"tooltip-" + menu.path}
              open={showTooltips[menu.path]}
              delayDuration={0} // We're handling the delay ourselves
            >
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  key={"path-" + menu.path}
                  className="focus:bg-accent relative"
                  disabled={menu.path === currentPage}
                  asChild
                  onMouseEnter={() => handleMouseEnter(menu.path)}
                  onMouseLeave={() => handleMouseLeave(menu.path)}
                >
                  <a href={"/" + menu.path}>{menu.name}</a>
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                align="center"
                className="bg-background border-primary text-foreground max-w-xs"
              >
                {menu.description}
              </TooltipContent>
            </Tooltip>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
};
